import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react'

import {
  AdditionalQuestionResponseType,
  AnswerType,
  QuestionResponseType,
} from '@hhgtech/hhg-components/types'
import { Stack, rem } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useIntl } from 'react-intl'

import { ADDITIONAL_QUESTION_PREFIX } from 'interfaces/constants/healthTools'
import { AdditionalQuestionFieldType } from 'interfaces/types'

import { generateSchema } from './helpers'
import { AdditionalQuestionField } from '../additionalQuestionField'

type Props = {
  className?: string
  disabledOther?: boolean
  additionalQuestions: Array<AdditionalQuestionFieldType>
  onChange: (v: AdditionalQuestionResponseType[]) => void
}

export type HandlerProps = {
  validate: (onValid: () => void, inValid?: () => void) => void
}

const AdditionalQuestionGroup: ForwardRefRenderFunction<HandlerProps, Props> = (
  { additionalQuestions, onChange, disabledOther, className },
  ref,
) => {
  const { formatMessage: f } = useIntl()
  const schema = useMemo(
    () =>
      generateSchema(
        f,
        additionalQuestions.map(
          (i) => ADDITIONAL_QUESTION_PREFIX + i.question.id,
        ),
      ),
    [additionalQuestions],
  )
  const form = useForm({
    validate: yupResolver(schema),
  })
  const getList = (value: any) => {
    const newResponseList: AdditionalQuestionResponseType[] = []
    additionalQuestions.map((i) => {
      const indexOfQuestion = newResponseList.findIndex(
        (j) => j.question.id === i.question.id,
      )
      const curSelectedAnswer: string[] = (
        value[ADDITIONAL_QUESTION_PREFIX + i.question.id] || ''
      )
        .split(',')
        .filter(Boolean)
      const listAnswerByIds: { [key in string]: AnswerType } = {}
      i.answers.map((i: AnswerType) => {
        listAnswerByIds[i.id] = i
      })
      const listAnswer: QuestionResponseType[] = []
      const otherVal: string[] = []
      curSelectedAnswer
        .map((j: string) => {
          if (listAnswerByIds[j]) {
            listAnswer.push({
              id: listAnswerByIds[j].id,
              value: listAnswerByIds[j].answer,
            })
          } else {
            otherVal.push(j)
          }
        })
        .filter(Boolean)
      const answers = [
        ...listAnswer,
        ...(otherVal.length > 0
          ? [
              {
                id: 0,
                value: otherVal?.[0],
              },
            ]
          : []),
      ].filter(Boolean)
      const response: AdditionalQuestionResponseType = {
        question: {
          id: Number(i.question.id),
          value: i.question.question,
        },
        answers,
      }
      if (indexOfQuestion !== -1) {
        if (response.answers.length > 0) {
          newResponseList[indexOfQuestion] = response
        } else {
          newResponseList.splice(indexOfQuestion, 1)
        }
      } else {
        response.answers.length > 0 && newResponseList.push(response)
      }
    })
    return newResponseList
  }

  useImperativeHandle(ref, () => ({
    validate: (onValid, onInvalid) => {
      form.validate()
      form.isValid() ? onValid?.() : onInvalid?.()
    },
  }))

  return (
    <Stack spacing={rem(12)} className={className}>
      {additionalQuestions.map((i, idx) => (
        <AdditionalQuestionField
          key={String(idx)}
          question={i}
          multiple={i.isMulti}
          inputProps={form.getInputProps(
            ADDITIONAL_QUESTION_PREFIX + i.question.id,
          )}
          disabledOther={disabledOther}
          onChange={(v) => {
            form.setFieldValue(
              ADDITIONAL_QUESTION_PREFIX + i.question.id,
              v.join(','),
            )
            onChange?.(
              getList({
                ...form.values,
                [ADDITIONAL_QUESTION_PREFIX + i.question.id]: v.join(','),
              }),
            )
          }}
        />
      ))}
    </Stack>
  )
}

export default forwardRef(AdditionalQuestionGroup)
